import { Chip, Stack } from "@mui/material";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { StatusChip } from "components/StatusChip";
import { gql } from "generated-graphql";
import {
  JobStatus,
  type ScoutCleanupJobsQuery,
  type ScoutCleanupJobsQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";
import { getJobStatusChipColor } from "routes/Staff/Jobs/utils";
import { prettyPrintDateTime } from "util/dates";

const SCOUT_CLEANUP_JOBS_QUERY = gql(`
  query ScoutCleanupJobs($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    scoutCleanupJobs(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        status
        createdAt
        createdBy
        updatedAt
        updatedBy
        latestLogStatus
        latestLogMessage
        latestLogCreatedAt
        expiredLocks
        orphanedLocks
        staleNodes
        staleNodeLocks
      }
    }
  }
`);

type ScoutCleanupJobRow = ScoutCleanupJobsQuery["scoutCleanupJobs"]["items"][number];

function formatCount(value: number | null | undefined) {
  return typeof value === "number" ? value.toLocaleString() : "";
}

export function ScoutCleanupJobs() {
  const columns: OmnisearchGridColDef<ScoutCleanupJobRow>[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "Job ID",
        flex: 1.2,
        minWidth: 220,
        filterKeyType: "text",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 150,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobStatus),
        renderCell({ value }) {
          return <StatusChip label={value} color={getJobStatusChipColor(value)} />;
        },
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "expiredLocks",
        headerName: "Expired Locks",
        flex: 0.7,
        minWidth: 130,
        filterKeyType: "number",
        renderCell({ value }) {
          return formatCount(value);
        },
      },
      {
        field: "orphanedLocks",
        headerName: "Orphaned Locks",
        flex: 0.8,
        minWidth: 150,
        filterKeyType: "number",
        renderCell({ value }) {
          return formatCount(value);
        },
      },
      {
        field: "staleNodes",
        headerName: "Stale Nodes",
        flex: 0.7,
        minWidth: 130,
        filterKeyType: "number",
        renderCell({ value }) {
          return formatCount(value);
        },
      },
      {
        field: "staleNodeLocks",
        headerName: "Stale Node Locks",
        flex: 0.8,
        minWidth: 160,
        filterKeyType: "number",
        renderCell({ value }) {
          return formatCount(value);
        },
      },
      {
        field: "latestLogStatus",
        headerName: "Latest Log Status",
        flex: 0.8,
        minWidth: 160,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(JobStatus),
        renderCell({ value }) {
          return value ? <Chip size="small" label={value} variant="outlined" /> : "";
        },
      },
      {
        field: "latestLogCreatedAt",
        headerName: "Latest Log",
        flex: 1,
        minWidth: 180,
        filterKeyType: "time",
        renderCell({ value }) {
          return value ? prettyPrintDateTime(value) : "";
        },
      },
      {
        field: "latestLogMessage",
        headerName: "Latest Message",
        flex: 1.4,
        minWidth: 240,
        filterKeyType: "text",
      },
      {
        field: "createdBy",
        headerName: "Created By",
        flex: 0.9,
        minWidth: 150,
        filterKeyType: "text",
      },
      {
        field: "updatedBy",
        headerName: "Updated By",
        flex: 0.9,
        minWidth: 150,
        filterKeyType: "text",
      },
    ],
    []
  );

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<ScoutCleanupJobsQuery, ScoutCleanupJobsQueryVariables, ScoutCleanupJobRow>
        isURLDriven
        persistenceKey="observe-scout-cleanup-jobs"
        columns={columns}
        dataQuery={SCOUT_CLEANUP_JOBS_QUERY}
        initialPageSize={50}
        initialSortModel={[{ field: "createdAt", sort: "desc" }]}
        getItems={(data) => data.scoutCleanupJobs.items}
        getCount={(data) => data.scoutCleanupJobs.count}
        noDataMessage="No Scout cleanup jobs found."
        gridSx={{
          minHeight: "500px",
        }}
      />
    </Stack>
  );
}
